import { useCallback, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useExtractionStore } from '@/stores/extraction-store'
import { useBidsStore } from '@/stores/bids-store'
import { extractedDeadlineToApply } from '@/lib/bid-deadline'
import { toast } from 'sonner'
import type { ExtractedDate } from '@/types/extraction'

// ===================
// Types
// ===================

interface KeyDateEditPopoverProps {
  item: ExtractedDate
  bidId: string
  trigger: ReactNode
  onSaved?: () => void
}

// ===================
// Helpers
// ===================

/** Slice an ISO or 'YYYY-MM-DD' value down to what `<input type="date">` expects. */
function toDateInput(value: string | null | undefined): string {
  if (!value) return ''
  return value.slice(0, 10)
}

/**
 * Did the save actually land? Read the row back out of the store after the
 * reload and compare it to what was sent.
 *
 * This is not belt-and-braces. `bulkPatchItems` settles its PATCHes and
 * resolves whatever happened, so a rejected write reaches this component as a
 * successful promise. The reload then restores the server's value, which means
 * the store — not the promise — is the only place the outcome is legible.
 *
 * The date is normalised through `toDateInput` on both sides: the API returns a
 * full ISO timestamp where the input holds `YYYY-MM-DD`, and comparing those
 * raw would fail every successful save. Time is compared trimmed, for the same
 * reason in reverse.
 */
function didWriteLand(itemId: string, dateValue: string, timeValue: string): boolean {
  const items = useExtractionStore.getState().categories.key_dates.items
  const saved = items.find((i) => (i as ExtractedDate).id === itemId) as
    | ExtractedDate
    | undefined
  if (!saved) return false
  return (
    toDateInput(saved.dateIso) === dateValue &&
    (saved.time ?? '').trim() === timeValue.trim()
  )
}

// ===================
// Component
// ===================

/**
 * In-place editor for a single key date's date and time.
 *
 * The commit is deliberately two calls, not one: the PATCH takes `timeText`
 * while the row on screen reads `time`, so the optimistic merge inside
 * `bulkPatchItems` would leave a stale time behind. Reloading the category is
 * what reconciles the two names.
 *
 * Editing the submission deadline is confirmed first — it moves the countdown
 * and, when the two disagree, the bid's own deadline, which every deadline-
 * driven surface in the app reads (sorting, overdue styling, My Work,
 * notifications). That is not a change to make silently from a table cell.
 */
export function KeyDateEditPopover({
  item,
  bidId,
  trigger,
  onSaved,
}: KeyDateEditPopoverProps) {
  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [dateValue, setDateValue] = useState(() => toDateInput(item.dateIso))
  const [timeValue, setTimeValue] = useState(() => item.time ?? '')
  const [isSaving, setIsSaving] = useState(false)

  const bulkPatchItems = useExtractionStore((s) => s.bulkPatchItems)
  const loadCategory = useExtractionStore((s) => s.loadCategory)

  const bid = useBidsStore((s) =>
    s.selectedBid?.id === bidId ? s.selectedBid : s.bids.find((b) => b.id === bidId) ?? null
  )
  const updateBid = useBidsStore((s) => s.updateBid)

  const fieldId = `key-date-${item.id ?? 'unsaved'}`

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        setDateValue(toDateInput(item.dateIso))
        setTimeValue(item.time ?? '')
      }
      setOpen(next)
    },
    [item.dateIso, item.time]
  )

  const commit = useCallback(async () => {
    if (!item.id) return
    setIsSaving(true)
    try {
      await bulkPatchItems(bidId, 'key_dates', [item.id], {
        dateIso: dateValue || null,
        timeText: timeValue || null,
      })
      await loadCategory(bidId, 'key_dates')
    } catch {
      toast.error('Could not save the date. Try again.')
      setIsSaving(false)
      return
    }

    // `bulkPatchItems` settles every PATCH and never rejects — a 500 on this
    // row only logs, then reloads the category and quietly restores the old
    // value. Without reading the row back, the catch above is dead code on the
    // exact failure it exists for, and the deadline branch below would move the
    // bid's deadline product-wide off a date the key date never accepted.
    if (!didWriteLand(item.id, dateValue, timeValue)) {
      toast.error('Could not save the date. Try again.')
      setIsSaving(false)
      return
    }

    // Only after the row itself is saved: the bid deadline follows the key
    // date, never the other way round.
    if (item.isSubmissionDeadline) {
      const extracted = extractedDeadlineToApply(dateValue || null, bid?.deadline ?? null)
      if (extracted) {
        try {
          await updateBid({ id: bidId, deadline: extracted.toISOString() })
          toast.success('Bid deadline updated')
        } catch {
          toast.error('Failed to update bid deadline')
        }
      }
    }

    setIsSaving(false)
    setConfirmOpen(false)
    setOpen(false)
    onSaved?.()
  }, [
    bid,
    bidId,
    bulkPatchItems,
    dateValue,
    item.id,
    item.isSubmissionDeadline,
    loadCategory,
    onSaved,
    timeValue,
    updateBid,
  ])

  const handleSave = useCallback(() => {
    if (item.isSubmissionDeadline) {
      // Close the popover first so the confirm owns the screen — and so its
      // Cancel is the only one on it.
      setOpen(false)
      setConfirmOpen(true)
      return
    }
    void commit()
  }, [commit, item.isSubmissionDeadline])

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start" side="bottom">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${fieldId}-date`}>Date</Label>
              <Input
                id={`${fieldId}-date`}
                type="date"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${fieldId}-time`}>Time (optional)</Label>
              <Input
                id={`${fieldId}-time`}
                type="text"
                value={timeValue}
                placeholder="2:00 pm AEDT"
                onChange={(e) => setTimeValue(e.target.value)}
              />
            </div>
            <p className="text-meta">Leave the date blank to clear it.</p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change the submission deadline?</AlertDialogTitle>
            <AlertDialogDescription>
              This moves the countdown and the bid deadline everywhere in this bid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isSaving}
              onClick={(e) => {
                // The default action closes the dialog immediately, which
                // unmounts the spinner mid-save.
                e.preventDefault()
                void commit()
              }}
            >
              Change deadline
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
